import {
  Container,
  Flex,
  Heading,
  Text,
  Stack,
  Box,
  Icon,
  HStack,
  VStack,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import NextLink from "next/link";
import {
  FiArrowRight,
  FiCheck,
  FiSmartphone,
  FiRadio,
  FiWifi,
} from "react-icons/fi";

// Design token constants — prevents duplicate-string lint errors
const TOKEN_TEXT_PRIMARY_LIGHT = "text.light-primary";
const TOKEN_TEXT_PRIMARY_DARK = "text.dark-primary";
const TOKEN_TEXT_MUTED_DARK = "text.dark-muted";
const TOKEN_ICON_BG_DARK = "rgba(255, 79, 0, 0.1)";

const DeviceCard = ({
  name,
  icon,
  description,
}: {
  name: string;
  icon: React.ElementType;
  description: string;
}) => {
  const bg = useColorModeValue("white", "neutral.900");
  const borderColor = useColorModeValue("neutral.100", "neutral.800");
  const nameColor = useColorModeValue(
    TOKEN_TEXT_PRIMARY_LIGHT,
    TOKEN_TEXT_PRIMARY_DARK
  );
  const descColor = useColorModeValue(
    "text.light-secondary",
    TOKEN_TEXT_MUTED_DARK
  );
  const iconBg = useColorModeValue("orange.50", TOKEN_ICON_BG_DARK);

  return (
    <Box
      bg={bg}
      p={5}
      borderRadius="md"
      border="1px solid"
      borderColor={borderColor}
    >
      <HStack spacing={4} align="start">
        <Box
          w={10}
          h={10}
          borderRadius="md"
          bg={iconBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon as={icon} color="brand.500" boxSize={5} />
        </Box>
        <VStack align="start" spacing={0.5}>
          <Text fontWeight={700} fontSize="md" color={nameColor}>
            {name}
          </Text>
          <Text fontSize="sm" color={descColor} lineHeight="1.5">
            {description}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default function Feature2() {
  const headingColor = useColorModeValue(
    TOKEN_TEXT_PRIMARY_LIGHT,
    TOKEN_TEXT_PRIMARY_DARK
  );
  const bodyColor = useColorModeValue(
    "text.light-secondary",
    TOKEN_TEXT_MUTED_DARK
  );
  const checkBg = useColorModeValue("orange.50", TOKEN_ICON_BG_DARK);
  const benefitColor = useColorModeValue(
    TOKEN_TEXT_PRIMARY_LIGHT,
    TOKEN_TEXT_PRIMARY_DARK
  );

  const devices = [
    {
      name: "Garmin inReach",
      icon: FiSmartphone,
      description: "Mini, Explorer, and Messenger devices supported",
    },
    {
      name: "ZOLEO",
      icon: FiWifi,
      description: "Full compatibility with ZOLEO satellite communicator",
    },
    {
      name: "Bivy Stick",
      icon: FiRadio,
      description: "Seamless integration with ACR Bivy Stick",
    },
  ];

  return (
    <Container maxW="6xl" py={{ base: 20, md: 28 }}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        align="center"
        justify="space-between"
        gap={{ base: 12, lg: 16 }}
      >
        {/* Left content */}
        <Stack spacing={8} maxW="xl" flex={1}>
          <Stack spacing={5}>
            <Heading
              color={headingColor}
              fontWeight={800}
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              lineHeight="1.2"
              letterSpacing="-0.02em"
            >
              Use Your Device{" "}
              <Text as="span" color="brand.500">
                As Intended
              </Text>
            </Heading>
            <Text
              color={bodyColor}
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
            >
              Communication is critical in remote areas. Use your personal
              satellite communicator to keep you safe — not just to call for
              help after an incident. We alert you to potential risks in
              advance, ensuring you&#39;re prepared.
            </Text>
          </Stack>

          <Stack spacing={3}>
            {[
              "No extra equipment needed",
              "Works with your existing device",
              "Automatic protection once registered",
            ].map((benefit) => (
              <HStack key={benefit} spacing={3}>
                <Box
                  w={5}
                  h={5}
                  borderRadius="full"
                  bg={checkBg}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={FiCheck} color="brand.500" boxSize={3} />
                </Box>
                <Text color={benefitColor} fontSize="md">
                  {benefit}
                </Text>
              </HStack>
            ))}
          </Stack>

          <Button
            as={NextLink}
            href="/login"
            bg="brand.500"
            color="white"
            px={8}
            py={6}
            fontSize="md"
            fontWeight={600}
            borderRadius="md"
            alignSelf="flex-start"
            rightIcon={<FiArrowRight />}
            _hover={{
              bg: "brand.600",
              transform: "translateY(-1px)",
              textDecoration: "none",
            }}
            transition="transform 0.2s ease, background-color 0.2s ease"
          >
            Create My Account
          </Button>
        </Stack>

        {/* Right content — device list */}
        <Stack flex={1} spacing={3} w="full" maxW={{ lg: "380px" }}>
          {devices.map((device) => (
            <DeviceCard
              key={device.name}
              name={device.name}
              icon={device.icon}
              description={device.description}
            />
          ))}
        </Stack>
      </Flex>
    </Container>
  );
}
